import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import MainInfoTab from './property/tabs/MainInfoTab';
import PublishingTab from './property/tabs/PublishingTab';
import { usePropertyForm } from '../../hooks/usePropertyForm';
import { FormProvider } from 'react-hook-form';

const PropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(id);
  
  const [activeTab, setActiveTab] = React.useState('details');
  
  const { 
    loading, 
    submitLoading,
    handleSubmit,
    formMethods,
    resetForm,
    stats
  } = usePropertyForm(id);

  // Reset form when navigating to new property page
  useEffect(() => {
    if (!isEditing && location.pathname === '/admin/properties/new') {
      resetForm();
    }
  }, [location.pathname, isEditing, resetForm]);

  if (loading) {
    return (
      <AdminLayout title={isEditing ? "Modifica Immobile" : "Nuovo Immobile"}>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento immobile...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? "Modifica Immobile" : "Nuovo Immobile"}>
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/properties')}
          className="text-gray-600 hover:text-gray-900 flex items-center"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna agli immobili
        </button>
      </div>

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Main Content */}
          {/* min-w-0: senza, i figli larghi (tabelle, editor) sfondano il flex container */}
          <div className="flex-grow min-w-0 space-y-6">
            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex gap-2 sm:gap-4 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-2.5 rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'details'
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Dettagli Immobile
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('publishing')}
                  className={`px-4 py-2.5 rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'publishing'
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Pubblicazione
                </button>
              </div>
            </div>

            {activeTab === 'details' ? (
              <MainInfoTab propertyId={id} />
            ) : (
              <PublishingTab propertyId={id} />
            )}
          </div>

          {/* Sidebar: sotto lg passa a colonna piena sotto il contenuto */}
          <div className="w-full lg:w-80 lg:flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              {/* Publication Status */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">Ordinamento</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priorità di visualizzazione (0-99)
                    </label>
                    <input
                      type="number"
                      {...formMethods.register('priority')}
                      min="0"
                      max="99"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary focus:border-primary"
                      placeholder="0 = massima priorità"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Valori più bassi hanno maggiore priorità. Gli immobili verranno visualizzati ordinati per questo valore.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                <div className="space-y-4">
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="w-full bg-primary hover:bg-primary-light text-white rounded-lg transition-colors flex items-center justify-center py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitLoading ? (
                      <>
                        <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Salvataggio...
                      </>
                    ) : (
                      <>
                        <Save className="h-5 w-5 mr-2" />
                        Salva Immobile
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => navigate('/admin/properties')}
                    className="w-full border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg py-3 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              </div>

              {/* Quick Stats */}
              {isEditing && (
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-700 mb-3">Statistiche Immobile</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Visite pagina:</span>
                      <span className="font-medium">{stats.pageViews}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Contatti ricevuti:</span>
                      <span className="font-medium">{stats.contacts}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Aggiunto ai preferiti:</span>
                      <span className="font-medium">{stats.favorites}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </AdminLayout>
  );
};

export default React.memo(PropertyForm);